import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { getUser } from '@paradigm/store/auth';
import DrfqNotificationsService, {
  FireNotificationFn,
  RfqNotificationFilter,
} from '#/unified-rfqs/services/drfq-notifications';
import { DrfqOrderbooksStore } from '#/unified-rfqs/store/drfq-orderbooks';
import { RfqFilters } from '#/unified-rfqs/services/rfq-filtering';
import { RfqId } from '../../entities/rfq';

interface Notifications {
  readonly fireNotification: FireNotificationFn;
  readonly hasNewNotifications: boolean;
  /**
   * Hand the markets page's live filter to the service that runs while the
   * page is closed, so leaving the page does not bring back the notifications
   * the filter was hiding. `null` gives the stored filter the say again, which
   * is what the page passes on its way out.
   */
  readonly setRfqNotificationFilter: (
    filter: RfqNotificationFilter | null,
  ) => void;
}

export const [useUnifiedMarketsNotifications, NotificationsCtxProvider] =
  createSafeContext<Notifications>('Notifications');

interface Props {
  readonly children: ReactNode;
  readonly playNotificationSound: () => void;
  readonly pinRfq: (rfqId: RfqId) => void;
  readonly fireNotification: (opts: {
    title: string;
    message: string | null;
    silent: boolean;
    icon?: string | (() => string);
    notificationId: string;
    onClick?: (() => void) | null;
  }) => void;
  readonly isViewingUmPage: boolean;
  /**
   * Reads the stored filter of the tab the markets page opens on. It holds
   * whenever the page has no live filter of its own to offer. Called on every
   * apply rather than captured, since the page rewrites the stored value as
   * the user edits it.
   */
  readonly getStoredRfqFilter?: () => RfqFilters | null;
}

export default function UnifiedMarketsNotificationsProvider({
  children,
  playNotificationSound,
  fireNotification,
  pinRfq,
  isViewingUmPage,
  getStoredRfqFilter,
}: Props) {
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [notificationsService, setNotificationsService] =
    useState<DrfqNotificationsService | null>(null);
  const user = useSelector(getUser);

  const serviceRef = useRef<DrfqNotificationsService | null>(null);
  const liveRfqFilterRef = useRef<RfqNotificationFilter | null>(null);
  /**
   * The mark prices the page last fetched. Stale marks still evaluate a
   * notional bound, and without any there is nothing to evaluate against at
   * all, so they outlive the live filter that brought them.
   */
  const drfqFutureInstrumentsRef = useRef<
    RfqNotificationFilter['drfqFutureInstruments']
  >([]);

  /**
   * The live filter wins wherever there is one. The markets page can be open
   * off the `/rfqs` route (the side panel), so which of the two is current is
   * not something the route can answer.
   */
  const applyRfqFilter = useCallback(() => {
    const liveRfqFilter = liveRfqFilterRef.current;
    if (liveRfqFilter != null) {
      serviceRef.current?.setRfqFilter(liveRfqFilter);
      return;
    }

    const storedFilters = getStoredRfqFilter?.() ?? null;
    serviceRef.current?.setRfqFilter(
      storedFilters == null
        ? null
        : {
            filters: storedFilters,
            drfqFutureInstruments: drfqFutureInstrumentsRef.current,
          },
    );
  }, [getStoredRfqFilter]);

  const setRfqNotificationFilter = useCallback(
    (filter: RfqNotificationFilter | null) => {
      liveRfqFilterRef.current = filter;
      if (filter != null) {
        drfqFutureInstrumentsRef.current = filter.drfqFutureInstruments;
      }
      applyRfqFilter();
    },
    [applyRfqFilter],
  );

  const playSound = useMemo(
    () =>
      debounce(() => {
        if (user.audio_notifications_enabled === true) {
          playNotificationSound();
        }
        // Dispatch notification sound at most every second
      }, 1000),
    [playNotificationSound, user],
  );

  const value: Notifications = useMemo(
    () => ({
      hasNewNotifications,
      setRfqNotificationFilter,
      fireNotification: (options) => {
        if (!isViewingUmPage) setHasNewNotifications(true);
        if ('onlyBadge' in options) return;
        if ('onlySound' in options) {
          playSound();
          return;
        }

        if (options.silent !== true) playSound();
        if (user.visual_notifications_enabled === false) {
          return;
        }

        fireNotification({ ...options, silent: true });
      },
    }),
    [
      hasNewNotifications,
      isViewingUmPage,
      user,
      playSound,
      fireNotification,
      setRfqNotificationFilter,
    ],
  );

  useEffect(() => {
    const orderbooksStore = new DrfqOrderbooksStore();
    const service = new DrfqNotificationsService({
      fireNotification: value.fireNotification,
      pinRfq,
      orderbooksStore,
    });
    serviceRef.current = service;
    applyRfqFilter();
    setNotificationsService(service);

    return () => {
      serviceRef.current = null;
      service.teardown();
    };
  }, [pinRfq, value.fireNotification, applyRfqFilter]);

  // Mark notifications as seen, when user goes to UM page
  useEffect(() => {
    if (isViewingUmPage) {
      setHasNewNotifications(false);
    }
  }, [isViewingUmPage]);

  /**
   * Start notifications service websocket when user is not at the UM
   * page. When the user goes to /rfqs, we shutdown the websocket connection
   * and use the one that was open by services/drfq-ws.
   */
  useEffect(() => {
    if (isViewingUmPage) {
      return;
    }
    notificationsService?.startWebsocket();
    return () => {
      notificationsService?.teardown();
    };
  }, [isViewingUmPage, notificationsService, value.fireNotification]);

  return (
    <NotificationsCtxProvider value={value}>
      {children}
    </NotificationsCtxProvider>
  );
}
